import type { LocalDate, LogbookEntry } from '@/lib/db';

import { confirmedOnly, filterLast90Days, sumTotals } from './aggregate';

/**
 * Currency and expiry tracking — `PLAN.md` §6 Phase 6, `NZ-LOGBOOK.md` §5.
 *
 * Every item here is a **reminder to check**, not authority — `NZ-LOGBOOK.md`
 * §5 is explicit that being wrong here is the most costly mistake this phase
 * can make, so every result carries the rule reference it is based on
 * (`ruleReference`) rather than asserting a bare pass/fail.
 *
 * Two different kinds of rule, because they need two different data sources:
 *
 * - **Recent experience** (day/night passenger-carrying currency) is
 *   *computed* from the entry set itself — landings already logged, folded
 *   the same way `aggregate.ts` folds everything else, but partitioned by
 *   aircraft type because that is what CAR 61.37 counts over.
 * - **Date-based reminders** (biennial flight review, medical, instrument
 *   currency, licence/rating expiry) have no signal in a flight's own data —
 *   nothing this app reads says when a medical was last issued — so they are
 *   a pilot-entered expiry date plus a countdown, not a derived number.
 *   `NZ-LOGBOOK.md` §5's "editable, with the rule reference shown" applies to
 *   the whole reminder, which is why `ExpiryReminder` carries its own label
 *   and reference rather than the four defaults being hardcoded facts.
 */

export type CurrencyLevel = 'ok' | 'expiring' | 'expired' | 'unknown';

const REQUIRED_LANDINGS = 3;
/** Exported so the page heading and the per-status `windowDays` cannot drift apart. */
export const RECENCY_WINDOW_DAYS = 90;

/**
 * How far back a type has to have been flown to still be worth a row.
 *
 * Recent experience is per type, so the row set is whatever the pilot has
 * been flying rather than a fixed list — and without a bound it would only
 * ever grow, leaving a type flown once in 2019 reading "not current" forever
 * beside the two types actually being flown. A year is long enough that a
 * type flown seasonally still shows its lapse, short enough that the page
 * stays a currency dashboard rather than a career history.
 */
const TYPE_LOOKBACK_DAYS = 365;

export interface RecentExperienceStatus {
  id: 'day-passenger-currency' | 'night-passenger-currency';
  label: string;
  ruleReference: string;
  level: Extract<CurrencyLevel, 'ok' | 'expired'>;
  count: number;
  required: number;
  windowDays: number;
  /** Set only when `level` is `'ok'`: the last calendar day this stays true without another qualifying landing. */
  lapsesOn?: LocalDate;
}

/** The day and night pair for one aircraft type, as flown. */
export interface TypeRecentExperience {
  /** The type as logged — the most recent spelling, where entries disagree on case. */
  aircraftType: string;
  /** The most recent confirmed entry for this type, which is what the rows are ordered by. */
  lastFlown: LocalDate;
  statuses: RecentExperienceStatus[];
}

export interface RecentExperienceReport {
  byType: TypeRecentExperience[];
  /**
   * Confirmed entries inside the 90-day window that landed somewhere but
   * carry no aircraft type. They cannot be counted toward any type's recent
   * experience and must not be silently dropped either — a pilot whose
   * landings all sit in untyped entries would otherwise read an empty page
   * as "nothing flown" rather than "type missing".
   */
  untyped: {
    entryCount: number;
    landingsDay: number;
    landingsNight: number;
  };
}

function addDays(date: LocalDate, days: number): LocalDate {
  const d = new Date(`${date}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The type as logged, or `null` for an entry that has none — a blank cell and an absent field are the same thing here. */
function loggedType(entry: LogbookEntry): string | null {
  const trimmed = entry.aircraftType?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

/**
 * The last day this recency requirement holds without another qualifying
 * landing: walk confirmed entries most-recent-first, accumulating the
 * landings that satisfy `required`, and the entry that tips the count over
 * the line is the one whose 90-day window expiring is what ends currency.
 */
function findLapseDate(
  entries: readonly LogbookEntry[],
  asOf: LocalDate,
  landingsOf: (entry: LogbookEntry) => number,
): LocalDate | null {
  const sorted = confirmedOnly(entries)
    .filter((entry) => entry.date <= asOf && landingsOf(entry) > 0)
    .sort((a, b) => b.date.localeCompare(a.date));

  let count = 0;
  for (const entry of sorted) {
    count += landingsOf(entry);
    if (count >= REQUIRED_LANDINGS) {
      return addDays(entry.date, RECENCY_WINDOW_DAYS + 1);
    }
  }
  return null;
}

/**
 * Day and night passenger-carrying recent experience for one type's entries.
 *
 * `LogbookLandings.day`/`.night` already include any touch-and-goes among
 * them, per that type's own contract, so no separate touch-and-go handling is
 * needed here.
 */
function statusesFor(
  typeEntries: readonly LogbookEntry[],
  asOf: LocalDate,
): RecentExperienceStatus[] {
  const windowTotals = sumTotals(filterLast90Days(typeEntries, asOf));

  const day: RecentExperienceStatus = {
    id: 'day-passenger-currency',
    label: 'Day passenger currency',
    ruleReference:
      'CAR 61.37 — 3 takeoffs and landings in an aircraft of the same type or class, in the preceding 90 days',
    level: windowTotals.landingsDay >= REQUIRED_LANDINGS ? 'ok' : 'expired',
    count: windowTotals.landingsDay,
    required: REQUIRED_LANDINGS,
    windowDays: RECENCY_WINDOW_DAYS,
  };
  const night: RecentExperienceStatus = {
    id: 'night-passenger-currency',
    label: 'Night passenger currency',
    ruleReference:
      'CAR 61.37 — 3 takeoffs and landings at night in an aircraft of the same type or class, in the preceding 90 days',
    level: windowTotals.landingsNight >= REQUIRED_LANDINGS ? 'ok' : 'expired',
    count: windowTotals.landingsNight,
    required: REQUIRED_LANDINGS,
    windowDays: RECENCY_WINDOW_DAYS,
  };

  if (day.level === 'ok') {
    const lapsesOn = findLapseDate(typeEntries, asOf, (entry) => entry.landings.day);
    if (lapsesOn !== null) {
      day.lapsesOn = lapsesOn;
    }
  }
  if (night.level === 'ok') {
    const lapsesOn = findLapseDate(typeEntries, asOf, (entry) => entry.landings.night);
    if (lapsesOn !== null) {
      night.lapsesOn = lapsesOn;
    }
  }

  return [day, night];
}

/**
 * Recent experience, **per aircraft type** — `NZ-LOGBOOK.md` §5.
 *
 * CAR 61.37 counts takeoffs and landings in an aircraft of the same type, so
 * a single figure folded over every entry is not a weaker answer but a wrong
 * one, and wrong in the one direction that matters: three landings spread
 * across three types reads as current on all three when it is current on
 * none. Each type therefore gets its own count, its own window and its own
 * lapse date.
 *
 * Types are compared case-insensitively — `C172` and `c172` are one type, and
 * the most recently logged spelling is the one shown — but never normalised
 * further than that. `PA28` and `Piper PA-28` stay apart, because deciding
 * they are the same aircraft would be this app asserting an airworthiness
 * fact it cannot know; the pilot edits the cell if they disagree.
 */
export function computeRecentExperience(
  entries: readonly LogbookEntry[],
  asOf: LocalDate,
): RecentExperienceReport {
  const lookbackStart = addDays(asOf, -(TYPE_LOOKBACK_DAYS - 1));
  const confirmed = confirmedOnly(entries).filter(
    (entry) => entry.date <= asOf && entry.date >= lookbackStart,
  );

  const groups = new Map<
    string,
    { aircraftType: string; lastFlown: LocalDate; entries: LogbookEntry[] }
  >();
  for (const entry of confirmed) {
    const type = loggedType(entry);
    if (type === null) {
      continue;
    }
    const key = type.toUpperCase();
    const group = groups.get(key);
    if (group === undefined) {
      groups.set(key, { aircraftType: type, lastFlown: entry.date, entries: [entry] });
      continue;
    }
    group.entries.push(entry);
    if (entry.date >= group.lastFlown) {
      // Most recent entry wins the spelling as well as the date, so a
      // corrected cell shows through rather than being outvoted by history.
      group.lastFlown = entry.date;
      group.aircraftType = type;
    }
  }

  const byType = [...groups.values()]
    .map((group) => ({
      aircraftType: group.aircraftType,
      lastFlown: group.lastFlown,
      statuses: statusesFor(group.entries, asOf),
    }))
    .sort(
      (a, b) =>
        b.lastFlown.localeCompare(a.lastFlown) || a.aircraftType.localeCompare(b.aircraftType),
    );

  const untypedInWindow = filterLast90Days(confirmed, asOf).filter(
    (entry) => loggedType(entry) === null && entry.landings.day + entry.landings.night > 0,
  );
  const untypedTotals = sumTotals(untypedInWindow);

  return {
    byType,
    untyped: {
      entryCount: untypedInWindow.length,
      landingsDay: untypedTotals.landingsDay,
      landingsNight: untypedTotals.landingsNight,
    },
  };
}

/** A pilot-entered date to count down to — biennial flight review, medical, instrument currency, licence/rating expiry, or anything else worth a reminder. */
export interface ExpiryReminder {
  id: string;
  label: string;
  ruleReference: string;
  /** `null` while unset — nothing to count down to yet, not a false "expired". */
  expiryDate: LocalDate | null;
  /** How many days out "expiring soon" starts showing. */
  warnWithinDays: number;
}

export interface ExpiryStatus {
  reminder: ExpiryReminder;
  level: CurrencyLevel;
  /** `null` when `expiryDate` is unset. Negative once past the expiry date. */
  daysRemaining: number | null;
}

/** Seeded once, the first time the currency page is opened with nothing stored yet — `NZ-LOGBOOK.md` §5's own candidate list. Every date starts unset; a pilot who doesn't fly the rating in question simply leaves it that way. */
export const DEFAULT_CURRENCY_REMINDERS: readonly ExpiryReminder[] = [
  {
    id: 'flight-review',
    label: 'Biennial flight review / competency check',
    ruleReference: 'CAR 61.39 — a flight review or competency check within the preceding 2 years',
    expiryDate: null,
    warnWithinDays: 60,
  },
  {
    id: 'medical',
    label: 'Medical certificate',
    ruleReference: 'CAR 67 — a current medical certificate for the class of licence held',
    expiryDate: null,
    warnWithinDays: 60,
  },
  {
    id: 'instrument',
    label: 'Instrument rating currency',
    ruleReference:
      'CAR 61.805 — instrument rating privileges: competency check or approach recency',
    expiryDate: null,
    warnWithinDays: 30,
  },
  {
    id: 'rating',
    label: 'Licence / type rating expiry',
    ruleReference: 'CAR 61 — rating validity as endorsed in the licence',
    expiryDate: null,
    warnWithinDays: 60,
  },
];

function daysBetween(from: LocalDate, to: LocalDate): number {
  const fromMs = Date.parse(`${from}T00:00:00.000Z`);
  const toMs = Date.parse(`${to}T00:00:00.000Z`);
  return Math.round((toMs - fromMs) / 86_400_000);
}

export function evaluateExpiryReminder(reminder: ExpiryReminder, asOf: LocalDate): ExpiryStatus {
  if (reminder.expiryDate === null) {
    return { reminder, level: 'unknown', daysRemaining: null };
  }
  const daysRemaining = daysBetween(asOf, reminder.expiryDate);
  const level: CurrencyLevel =
    daysRemaining < 0 ? 'expired' : daysRemaining <= reminder.warnWithinDays ? 'expiring' : 'ok';
  return { reminder, level, daysRemaining };
}

export function evaluateExpiryReminders(
  reminders: readonly ExpiryReminder[],
  asOf: LocalDate,
): ExpiryStatus[] {
  return reminders.map((reminder) => evaluateExpiryReminder(reminder, asOf));
}
